import { Button, css, DiagramIcon, PlayCircleIcon, styled, useTheme } from 'canoe';
import React from 'react';
import { Modal } from 'styleguide';

import { useEventing } from 'commons/eventing';
import { useToggle } from 'commons/hooks';
import { useFlowGraph } from 'craft/state';
import { useFlow } from 'craft/state/flows/useFlow';

import { ChartGraph } from './ChartGraph';
import { PreviewFlow } from './PreviewFlow';

const FlowButtonsWrapper = styled.div<{ spaceAfter?: boolean }>`
  ${({ theme, spaceAfter }) => css`
    display: flex;
    gap: ${theme.spaces.xxs};
    height: 32px;
    margin-bottom: ${theme.spaces.xxs};
    ${spaceAfter &&
    css`
      margin-bottom: ${theme.spaces.xs};
    `}
  `}
`;

interface FlowActionButtonsProps {
  nodeId: string;
  spaceAfter?: boolean;
}

export const FlowActionButtons = React.memo(({ nodeId, spaceAfter }: FlowActionButtonsProps) => {
  const theme = useTheme();
  const [isOpenPreview, togglePreview] = useToggle();
  const [isOpenFlowChart, toggleFlowChart] = useToggle();
  const graph = useFlowGraph(nodeId);
  const { stateId, activeStepId } = useFlow(nodeId);
  const { track } = useEventing();

  return (
    <FlowButtonsWrapper spaceAfter={spaceAfter}>
      {isOpenPreview && (
        <Modal isOpen size="xl" toggle={togglePreview}>
          <Modal.Body>
            <div css={{ padding: theme.spaces.normal }}>
              <PreviewFlow stateId={stateId} isSdkPreview />
            </div>
          </Modal.Body>
        </Modal>
      )}

      {isOpenFlowChart && (
        <Modal
          isOpen
          size="xxl"
          toggle={toggleFlowChart}
          containerStyles={{
            width: '90%',
            minWidth: '65em',
            minHeight: '90%',
            backgroundColor: theme.colors.OnyxGrayD10,
          }}
        >
          <Modal.Body css={{ backgroundColor: theme.colors.OnyxGrayD10, maxHeight: '100%' }}>
            <ChartGraph graph={graph} activeStepId={activeStepId} nodeId={nodeId} />
          </Modal.Body>
        </Modal>
      )}

      <Button
        onClick={() => {
          track('editor.flow.preview');
          togglePreview();
        }}
        icon={PlayCircleIcon}
        fullWidth
        size="xsmall"
      >
        Preview flow
      </Button>
      <Button
        fullWidth
        onClick={() => {
          track('editor.flow.chart');
          toggleFlowChart();
        }}
        icon={DiagramIcon}
        size="xsmall"
      >
        View Flow Chart
      </Button>
    </FlowButtonsWrapper>
  );
});
